import React, { useEffect } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import OrbitalCalc from "@/pages/OrbitalCalc";
import ManualTigCalc from "@/pages/ManualTigCalc";
import AIAdvisor from "@/pages/AIAdvisor";
import Community from "@/pages/Community";
import Pricing from "@/pages/Pricing";
import Profile from "@/pages/Profile";
import History from "@/pages/History";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Admin from "@/pages/Admin";
import Pipefitter from "@/pages/Pipefitter";
import PipeVisualizer from "@/pages/PipeVisualizer";
import SharedIsometry from "@/pages/SharedIsometry";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Jobs from "@/pages/Jobs";
import PersonaBlog from "@/pages/PersonaBlog";
import AuthCallback from "@/pages/AuthCallback";
import BillingForm from "@/pages/BillingForm";
import FAQ from "@/pages/FAQ";
import RealPrograms from "@/pages/RealPrograms";
import PublicProfile from "@/pages/PublicProfile";
import Tour from "@/pages/Tour";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import CommunityRules from "@/pages/CommunityRules";
import ChildSafety from "@/pages/ChildSafety";
import QuizIndex from "@/pages/QuizIndex";
import Quiz from "@/pages/Quiz";
import WallOfFame from "@/pages/WallOfFame";
import AboutCertificate from "@/pages/AboutCertificate";
import PushDebug from "@/pages/PushDebug";
import CookiesBanner from "@/components/CookiesBanner";
import PushOnboardingModal from "@/components/PushOnboardingModal";
import useAnonPresence from "@/hooks/useAnonPresence";

function AppRouter() {
  useAnonPresence(); // count anonymous browsers toward online tally
  const location = useLocation();
  const navigate = useNavigate();

  // Listen for TIGIK_NAV messages coming from the service worker when the user
  // taps a push notification while the app is already running. Without this,
  // React Router state stays where it is and the user doesn't land on the
  // notification's target URL (e.g. Tigik News).
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMsg = (event) => {
      const data = event.data || {};
      if (data.type === "TIGIK_NAV" && typeof data.url === "string") {
        try { navigate(data.url); } catch (_) { window.location.href = data.url; }
      }
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, [navigate]);
  // Handle OAuth callback - check URL fragment SYNCHRONOUSLY (not in effect)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/calculators/orbital" element={<OrbitalCalc />} />
        <Route path="/programs/real" element={<RealPrograms />} />
        <Route path="/calculators/manual-tig" element={<ManualTigCalc />} />
        <Route path="/calculators/pipefitter" element={<Pipefitter />} />
        <Route path="/pipe-visualizer" element={<PipeVisualizer />} />
        <Route path="/izometria/:id" element={<SharedIsometry />} />
        <Route path="/ai-advisor" element={<AIAdvisor />} />
        <Route path="/community" element={<Community />} />
        <Route path="/push-debug" element={<PushDebug />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/profile/billing" element={<BillingForm />} />
        <Route path="/u/:userId" element={<PublicProfile />} />
        <Route path="/tour" element={<Tour />} />
        <Route path="/history" element={<History />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/blog/:slug" element={<PersonaBlog />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/community-rules" element={<CommunityRules />} />
        <Route path="/child-safety" element={<ChildSafety />} />
        <Route path="/quiz" element={<QuizIndex />} />
        <Route path="/quiz/:quizType" element={<Quiz />} />
        <Route path="/wall-of-fame" element={<WallOfFame />} />
        <Route path="/co-je-sa-eko" element={<AboutCertificate />} />
        <Route path="/about-certificate" element={<AboutCertificate />} />
      </Routes>
      <CookiesBanner />
    </Layout>
  );
}

export default function App() {
  // Clear PWA launcher-icon badge whenever the app comes into the foreground.
  // Also clears it whenever the SW says "open notifications dismissed" via message.
  useEffect(() => {
    const clearBadge = () => {
      if (typeof navigator !== "undefined" && "clearAppBadge" in navigator) {
        navigator.clearAppBadge().catch(() => {});
      }
      // Tell the SW to close any stale notifications too
      if (typeof navigator !== "undefined" && navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage({ type: "CLEAR_APP_BADGE" });
      }
    };
    const onVisible = () => { if (!document.hidden) clearBadge(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", clearBadge);
    clearBadge(); // run once on initial load too
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", clearBadge);
    };
  }, []);

  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <PushOnboardingModal />
          <Toaster theme="dark" position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </LangProvider>
  );
}
